import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lightbulb, Search, ListChecks, Star, Trophy, Code2, CheckCircle2, Users, ArrowUpRight, CalendarClock, Newspaper, BarChart3, Link2, MousePointerClick, UserPlus, ChevronRight } from "lucide-react";
import { api } from "../../lib/api";
import { useLang } from "../../context/LanguageContext";
import PartnerFiche from "../../components/PartnerFiche";
import { SOCIAL_NETWORKS } from "../../lib/socials";

const PTYPE = { blog: "Blog", site: "Site web", influenceur: "Influenceur", autre: "Autre" };
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const [s, setS] = useState(null);
  const [partners, setPartners] = useState([]);
  const [ficheFor, setFicheFor] = useState(null);
  const nav = useNavigate();
  const { tf: t } = useLang();
  useEffect(() => { api.get("/admin/stats").then((r) => setS(r.data)).catch(() => {}); }, []);
  const loadPartners = () => api.get("/admin/partners").then((r) => setPartners(r.data)).catch(() => {});
  useEffect(() => { loadPartners(); }, []);

  const cards = s ? [
    { v: s.new_ideas, l: t("dash_new_ideas"), to: "IDÉE DÉPOSÉE", icon: Lightbulb, color: "#00F0FF" },
    { v: s.in_study, l: t("dash_in_study"), to: "EN ÉTUDE", icon: Search, color: "#0A84FF" },
    { v: s.preselected, l: t("dash_presel"), to: "PRÉSÉLECTIONNÉE", icon: ListChecks, color: "#5E5CE6" },
    { v: s.finalists, l: t("dash_finalists"), to: "FINALISTE", icon: Star, color: "#FFD60A" },
    { v: s.selected, l: t("dash_selected"), to: "SÉLECTIONNÉE", icon: Trophy, color: "#30D158" },
    { v: s.in_development, l: t("dash_in_dev"), to: null, icon: Code2, color: "#0A84FF" },
    { v: s.finished, l: t("dash_finished"), to: "APPLICATION TERMINÉE", icon: CheckCircle2, color: "#30D158" },
    { v: s.users, l: t("dash_users"), to: "users", icon: Users, color: "#BF5AF2" },
  ] : [];

  const go = (c) => c.to === "users" ? nav("/admin/utilisateurs") : nav(`/admin/projets${c.to ? `?status=${encodeURIComponent(c.to)}` : ""}`);

  const quick = [
    { l: "Gestion de la semaine", d: "Configurer le challenge", to: "/admin/semaine", icon: CalendarClock },
    { l: "Marketing Presse", d: "Contacts & envois", to: "/admin/presse", icon: Newspaper },
    { l: "Analyse du trafic", d: "Statistiques du site", to: "/admin/analyse", icon: BarChart3 },
  ];

  const totalSignups = partners.reduce((a, p) => a + (p.signups || 0), 0);
  const totalClicks = partners.reduce((a, p) => a + (p.clicks || 0), 0);

  return (
    <div className="mx-auto max-w-6xl">
      {/* En-tête */}
      <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-surface p-8">
        <div className="pointer-events-none absolute -right-20 -top-24 h-64 w-64 rounded-full bg-cyan/20 blur-[100px]" />
        <div className="pointer-events-none absolute -bottom-24 left-1/3 h-56 w-56 rounded-full bg-electric/15 blur-[110px]" />
        <div className="relative">
          <div className="text-[11px] uppercase tracking-[0.25em] text-cyan">{t("dash_kicker")}</div>
          <h1 className="mt-3 font-display text-3xl font-900 text-white sm:text-4xl">{t("dash_title")}</h1>
          <p className="mt-3 max-w-xl text-sm leading-relaxed text-ink-secondary">
            Vue d&apos;ensemble des demandes et de l&apos;activité de APP CHALLENGE ACADEMY. Cliquez sur une carte pour filtrer les dossiers correspondants.
          </p>
        </div>
      </div>

      {/* Onglets : tout est fermé par défaut, clic = dérouler, reclic = fermer */}

      {/* Cartes statistiques */}
      {/* Section « stats » — affichée en permanence (accordéon supprimé) */}
      <>
        <div className="mt-8 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan" data-testid="section-title-stats">Statistiques</div>
      <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="dash-panel-stats">
        {cards.map((c, i) => (
          <motion.button
            key={i} onClick={() => go(c)} data-testid={`admin-stat-${i}`}
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05, duration: 0.4 }}
            className="group relative overflow-hidden rounded-xl border border-white/10 bg-void p-5 text-left transition-all hover:-translate-y-1 hover:border-white/25"
          >
            <span className="absolute inset-x-0 top-0 h-0.5" style={{ background: c.color }} />
            <div className="flex items-start justify-between">
              <span className="flex h-11 w-11 items-center justify-center rounded-lg" style={{ background: `${c.color}1f`, color: c.color }}>
                <c.icon size={20} />
              </span>
              <ArrowUpRight size={16} className="text-ink-tertiary opacity-0 transition-opacity group-hover:opacity-100" />
            </div>
            <div className="mt-4 font-display text-4xl font-900 tabular-nums text-white">{c.v}</div>
            <div className="mt-1 text-sm text-ink-secondary">{c.l}</div>
          </motion.button>
        ))}
      </div>
      </>

      {/* Accès rapides */}
      {/* Section « acces » — affichée en permanence (accordéon supprimé) */}
      <>
        <div className="mt-8 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan" data-testid="section-title-acces">Accès rapides</div>
      <div data-testid="dash-panel-acces">
      <div className="mt-6 grid gap-4 sm:grid-cols-3">
        {quick.map((q, i) => (
          <motion.button
            key={q.to} onClick={() => nav(q.to)} data-testid={`admin-quick-${i}`}
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 + i * 0.06 }}
            className="group flex items-center gap-4 rounded-xl border border-white/10 bg-surface p-5 text-left transition-all hover:-translate-y-1 hover:border-cyan/40"
          >
            <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-cyan/10 text-cyan transition-colors group-hover:bg-cyan group-hover:text-void">
              <q.icon size={20} />
            </span>
            <div className="min-w-0 flex-1">
              <div className="font-display text-base font-700 text-white">{q.l}</div>
              <div className="text-xs text-ink-secondary">{q.d}</div>
            </div>
            <ArrowUpRight size={16} className="text-ink-tertiary transition-all group-hover:text-cyan" />
          </motion.button>
        ))}
      </div>
      </div>
      </>

      {/* Espace Partenaires : liste cliquable -> ouverture de la fiche */}
      {/* Section « partenaires » — affichée en permanence (accordéon supprimé) */}
      <>
        <div className="mt-8 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan" data-testid="section-title-partenaires">Partenaires</div>
      <div className="mt-6" data-testid="dash-partners">
        <div className="flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-[0.2em] text-ink-tertiary">Partenaires</div>
          <button onClick={() => nav("/admin/liens")} data-testid="dash-partners-manage" className="inline-flex items-center gap-1 text-xs text-cyan hover:underline">
            Gérer les liens marketing <ChevronRight size={13} />
          </button>
        </div>
        {partners.length > 0 && (
          <div className="mt-3 flex gap-4 text-xs text-ink-secondary">
            <span className="inline-flex items-center gap-1"><MousePointerClick size={13} className="text-cyan" /> {totalClicks} clic{totalClicks > 1 ? "s" : ""}</span>
            <span className="inline-flex items-center gap-1"><UserPlus size={13} className="text-green-400" /> {totalSignups} inscrit{totalSignups > 1 ? "s" : ""}</span>
          </div>
        )}
        {partners.length === 0 ? (
          <button onClick={() => nav("/admin/liens")} data-testid="dash-partners-empty"
            className="mt-4 w-full rounded-2xl border border-dashed border-white/15 p-8 text-center text-sm text-ink-tertiary transition-colors hover:border-cyan/40 hover:text-white">
            Aucun partenaire. Cliquez pour créer votre premier lien marketing.
          </button>
        ) : (
          <div className="mt-4 space-y-2">
            {partners.map((p) => {
              const socs = SOCIAL_NETWORKS.filter((n) => (p.socials || {})[n.key]);
              return (
                <button key={p.id} onClick={() => setFicheFor(p)} data-testid={`dash-partner-${p.id}`}
                  className="group flex w-full flex-wrap items-center gap-3 rounded-xl border border-white/10 bg-surface px-4 py-3 text-left transition-all hover:border-cyan/40">
                  {p.has_logo ? (
                    <img src={`${BACKEND}/api/partners/${p.id}/logo`} alt={p.name} className="h-9 w-9 shrink-0 rounded-lg object-cover" data-testid={`dash-partner-logo-${p.id}`} />
                  ) : (
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-cyan/10 text-cyan"><Link2 size={16} /></span>
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="truncate font-display text-sm font-700 text-white">{p.name}</span>
                      <span className="rounded-full bg-electric/20 px-2 py-0.5 text-[9px] font-600 uppercase text-electric">{PTYPE[p.ptype] || p.ptype}</span>
                      {!p.active && <span className="rounded-full bg-red-500/15 px-2 py-0.5 text-[9px] font-600 uppercase text-red-400">Inactif</span>}
                    </span>
                    <span className="mt-0.5 block text-[11px] text-ink-tertiary">/r/{p.code}</span>
                  </span>
                  {socs.length > 0 && (
                    <span className="flex shrink-0 items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                      {socs.slice(0, 5).map((n) => (
                        <a key={n.key} href={p.socials[n.key]} target="_blank" rel="noopener noreferrer" title={n.label} data-testid={`dash-soc-${p.id}-${n.key}`}
                          className="flex h-7 w-7 items-center justify-center rounded-lg border border-white/15 text-ink-secondary transition-colors hover:border-cyan hover:text-cyan">
                          <n.icon size={13} />
                        </a>
                      ))}
                    </span>
                  )}
                  <span className="flex shrink-0 items-center gap-3 text-xs text-ink-secondary">
                    <span className="inline-flex items-center gap-1"><MousePointerClick size={13} className="text-cyan" />{p.clicks}</span>
                    <span className="inline-flex items-center gap-1"><UserPlus size={13} className="text-green-400" />{p.signups}</span>
                  </span>
                  <ChevronRight size={16} className="shrink-0 text-ink-tertiary transition-colors group-hover:text-cyan" />
                </button>
              );
            })}
          </div>
        )}
      </div>
      </>

      {ficheFor && (
        <PartnerFiche partner={ficheFor} onClose={() => setFicheFor(null)} onSaved={() => { loadPartners(); }} />
      )}
    </div>
  );
}
